import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { SalvButton, SalvModal, SalvProgressStepper, Size, Step } from '@salvapp/ui-components';
import {
  getSnoozeEventInformation,
  getStatusInformation,
  ScreeningAuditEventDescription,
  ScreeningCheckMatch,
  SnoozeNotePreview,
} from './personStatusesHistoryHelper';
import { formatDate } from '../common/datetime/FormattedDateTime';
import {
  InternalApiDocumentEvent,
  InternalApiPersonCategory,
  InternalApiPersonEvents,
  InternalApiPersonScreeningAuditEvent,
  InternalApiPersonStatusEvent,
  InternalApiSanctionCheckEvent,
  InternalApiSnoozeEvent,
} from '../api-internal';
import { personInternalApi } from '../apis';
import { Spinner } from '../feedback/Spinner';
import { getScreeningFlowDisplayId } from '../screening/configure/new/helper';
import { useMe } from '../organisation/useMe';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';

interface ComponentProps {
  personId: string;
  category: InternalApiPersonCategory | undefined;
}

interface HistoryEvent {
  type: HistoryEventType;
  startTime?: Date;
  status?: InternalApiPersonStatusEvent;
  snoozeEvent?: InternalApiSnoozeEvent;
  latestSanctionCheck?: InternalApiSanctionCheckEvent;
  flow?: InternalApiPersonScreeningAuditEvent;
  documentEvent?: InternalApiDocumentEvent;
}

enum HistoryEventType {
  STATUS,
  SNOOZE,
  SANCTION_CHECK,
  SCREENING_FLOW,
  DOCUMENT_DELETED,
}

export const PersonStatusesHistory = ({ personId, category }: ComponentProps) => {
  const [showModal, setShowModal] = useState(false);
  const [history, setHistory] = useState<InternalApiPersonEvents | null>(null);
  const { features } = useMe();
  const documentDeletionHistoryEnabled = isFeatureEnabled(ORGANISATION_FEATURE.DOCUMENT_DELETION_HISTORY, features);

  const fetchHistory = useCallback(async () => {
    const history = await personInternalApi.findPersonHistoryEvents({ personId });
    setHistory(history);
  }, [personId]);

  useEffect(() => {
    if (showModal) {
      setHistory(null);
      fetchHistory().catch((error) => {
        console.error('Failed to load person history:', error);
      });
    }
  }, [personId, showModal, fetchHistory]);

  const renderedEvents = useMemo(() => {
    if (!history) {
      return [];
    }
    const { statuses, snoozeHistory, latestSanctionCheck, flows, documentEvents } = history;
    const events: HistoryEvent[] = [];
    events.push(...statuses.map((status) => toStatusEvent(status)));
    events.push(...snoozeHistory.map((snoozeEvent) => toSnoozeEvent(snoozeEvent)));
    flows && events.push(...flows.map((flow) => toFlowEvent(flow)));

    latestSanctionCheck && events.push(toSanctionCheckEvent(latestSanctionCheck));
    if (documentDeletionHistoryEnabled) {
      documentEvents && events.push(...documentEvents.map((doc) => toDocumentEvent(doc)));
    }

    const sortedEvents = events.sort(
      (a, b) => (b.startTime?.getTime() ?? Infinity) - (a.startTime?.getTime() ?? Infinity)
    );

    return sortedEvents.map((event, index) => {
      switch (event.type) {
        case HistoryEventType.STATUS: {
          const statusEvent = event.status;
          return (
            statusEvent && (
              <Step
                key={index}
                header={getStatusInformation({
                  statusEvent,
                  index: index,
                  length: sortedEvents.length,
                })}
                description={formatDate(statusEvent.startTime, true)}
              />
            )
          );
        }
        case HistoryEventType.SNOOZE: {
          const snoozeEvent = event.snoozeEvent;
          return (
            snoozeEvent && (
              <Step
                key={index}
                header={getSnoozeEventInformation(snoozeEvent)}
                description={
                  snoozeEvent.snoozeConfig.createdTime && formatDate(snoozeEvent.snoozeConfig.createdTime, true)
                }
              >
                <SnoozeNotePreview event={snoozeEvent} />
              </Step>
            )
          );
        }
        case HistoryEventType.SANCTION_CHECK: {
          const latestSanctionCheck = event.latestSanctionCheck;
          return (
            latestSanctionCheck && (
              <Step
                key={index}
                header='Latest screening check'
                description={formatDate(latestSanctionCheck.createdTime, true)}
              >
                <ScreeningCheckMatch event={latestSanctionCheck} />
              </Step>
            )
          );
        }
        case HistoryEventType.SCREENING_FLOW: {
          const flow = event.flow;
          return (
            flow && (
              <Step key={index} header={`Included in screening flow ${getScreeningFlowDisplayId(flow.flowId!)}`}>
                <div className='description'>
                  <ScreeningAuditEventDescription flow={flow} />
                </div>
              </Step>
            )
          );
        }
        case HistoryEventType.DOCUMENT_DELETED: {
          const action = 'removed file';
          const fileName = event.documentEvent?.fileName || 'unknown file';
          const user = event.documentEvent?.deletedBy?.name || event.documentEvent?.createdBy?.name || 'System';
          const deletedTime: Date =
            event.documentEvent?.deletedTime ?? event.documentEvent?.createdTime ?? event.startTime!;
          const uploadedBy = event.documentEvent?.createdBy?.name;
          const uploadedTime = event.documentEvent?.createdTime;
          return (
            <Step key={index} header={`${user} ${action}: ${fileName}`} description={formatDate(deletedTime, true)}>
              {uploadedBy && uploadedTime && (
                <div className={'m-t-12'}>
                  <div className={'text-xs'} style={{ color: 'var(--text-medium)' }}>
                    Uploaded by {uploadedBy} on {formatDate(uploadedTime, true)}
                  </div>
                </div>
              )}
              {!uploadedBy && uploadedTime && (
                <div className={'m-t-12'}>
                  <div className={'text-xs'} style={{ color: 'var(--text-medium)' }}>
                    Uploaded on {formatDate(uploadedTime, true)}
                  </div>
                </div>
              )}
            </Step>
          );
        }
      }
    });
  }, [history, documentDeletionHistoryEnabled]);
  const personCategory = category === InternalApiPersonCategory.COUNTERPARTY ? 'Counterparty' : 'Customer';

  return (
    <>
      <Button className='dropdown-item' onClick={() => setShowModal(true)}>
        View history
      </Button>
      <SalvModal show={showModal} onHide={() => setShowModal(false)}>
        <SalvModal.Header closeButton>{personCategory} history</SalvModal.Header>
        <SalvModal.Body>
          {history && renderedEvents.length > 0 && (
            <SalvProgressStepper activeStep={1}>{renderedEvents}</SalvProgressStepper>
          )}
          {history && renderedEvents.length === 0 && (
            <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-medium)' }}>No history events</div>
          )}
          {!history && <Spinner loading={true} small={true} />}
        </SalvModal.Body>

        <SalvModal.Footer>
          <SalvButton
            data-testid='close'
            variant='muted'
            onClick={() => setShowModal(false)}
            label='Close'
            size={Size.MEDIUM}
          />
        </SalvModal.Footer>
      </SalvModal>
    </>
  );
};

const toStatusEvent = (status: InternalApiPersonStatusEvent): HistoryEvent => {
  return {
    type: HistoryEventType.STATUS,
    startTime: status.startTime,
    status,
  };
};

const toSnoozeEvent = (snoozeEvent: InternalApiSnoozeEvent): HistoryEvent => {
  return {
    type: HistoryEventType.SNOOZE,
    startTime: snoozeEvent.snoozeConfig.createdTime,
    snoozeEvent,
  };
};

const toSanctionCheckEvent = (latestSanctionCheck: InternalApiSanctionCheckEvent): HistoryEvent => {
  return {
    type: HistoryEventType.SANCTION_CHECK,
    startTime: latestSanctionCheck.createdTime,
    latestSanctionCheck,
  };
};

const toFlowEvent = (flow: InternalApiPersonScreeningAuditEvent): HistoryEvent => {
  return {
    type: HistoryEventType.SCREENING_FLOW,
    startTime: flow.startTime,
    flow,
  };
};

const toDocumentEvent = (doc: InternalApiDocumentEvent): HistoryEvent => {
  return {
    type: HistoryEventType.DOCUMENT_DELETED,
    startTime: doc.deletedTime ?? doc.createdTime,
    documentEvent: doc,
  };
};
